import React from "react";
import {
  Activity,
  ArrowDownWideNarrow,
  ArrowLeft,
  ArrowRight,
  ArrowRightToLine,
  ArrowUpWideNarrow,
  BadgeCheck,
  Blend,
  BookOpen,
  Bookmark,
  Bot,
  Bug,
  Calendar,
  Camera,
  ChartColumn,
  ChartLine,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  CircleDot,
  CircleHelp,
  CircleUser,
  Clock,
  Copy,
  CornerDownLeft,
  Cpu,
  Download,
  Ellipsis,
  EllipsisVertical,
  Eye,
  EyeOff,
  FileText,
  Filter,
  GripVertical,
  House,
  Inbox,
  Kanban,
  Layers,
  LayoutGrid,
  Link,
  List,
  ListChecks,
  LoaderCircle,
  Lock,
  MessageCircle,
  LogOut,
  Logs,
  Mail,
  Maximize,
  Minimize,
  Minus,
  Moon,
  MoveDiagonal2,
  Pause,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  RotateCw,
  Scroll,
  ScrollText,
  Search,
  Settings,
  Settings2,
  ShieldCheck,
  SlidersVertical,
  Sparkle,
  SquareCheckBig,
  Star,
  Sun,
  Trash2,
  TriangleAlert,
  Unlink,
  Upload,
  User,
  Users,
  Variable,
  X,
  Zap,
} from "lucide-react";

/**
 * Shared icon set: every icon is a Lucide (lucide-react) glyph. Generic ones
 * are drawn at this app's 1.8 stroke; the Jira / Xray issue-type icons below
 * keep their Jira colours. Every icon accepts standard SVG props (className,
 * etc.). Callers use Icons.X only, never lucide-react directly,
 * so the stroke, the names and any swap stay in this one file.
 */
function lucide(Glyph, fixed = {}) {
  function Icon(props) {
    return (
      <Glyph
        strokeWidth={1.8}
        aria-hidden={props["aria-label"] ? undefined : "true"}
        {...fixed}
        {...props}
      />
    );
  }
  Icon.displayName = `Icon(${Glyph.displayName || "Lucide"})`;
  return Icon;
}
/* Filled glyphs (Play, Pause, Spark) keep their solid look. */
const SOLID = { fill: "currentColor" };

/* Jira / Xray issue-type icons: Lucide glyphs in each type's OWN Jira colour
 * (not currentColor), so a type looks the same on every screen, and every one
 * renders at 20px: any size class a caller passes (h-*, w-*, size-*) is
 * dropped so they can never drift apart. `muted` greys an icon out for empty
 * states. Jira's types are outline glyphs (`jiraIcon`); Xray's are a white
 * glyph on a filled rounded square (`xrayIcon`), as Xray draws them. */
const SIZE_CLASS = /(^|\s)(?:h|w|size|min-h|min-w|max-h|max-w)-\S+/g;
function jiraClass(className, muted, extra = "") {
  return [
    "h-5 w-5 flex-none",
    extra,
    String(className).replace(SIZE_CLASS, " ").trim(),
    muted ? "opacity-40 grayscale" : "",
  ]
    .filter(Boolean)
    .join(" ");
}
function jiraIcon(Glyph, color) {
  function JiraIcon({ className = "", muted = false, ...rest }) {
    return (
      <Glyph
        size={20}
        color={color}
        strokeWidth={1.9}
        aria-hidden={rest["aria-label"] ? undefined : "true"}
        {...rest}
        className={jiraClass(className, muted)}
      />
    );
  }
  return JiraIcon;
}
function xrayIcon(Glyph, color, glyphProps = {}) {
  function XrayIcon({ className = "", muted = false, style, ...rest }) {
    return (
      <span
        aria-hidden={rest["aria-label"] ? undefined : "true"}
        role={rest["aria-label"] ? "img" : undefined}
        {...rest}
        style={{ backgroundColor: color, ...style }}
        className={jiraClass(className, muted, "inline-flex items-center justify-center rounded")}
      >
        <Glyph size={13} color="#fff" strokeWidth={2.8} aria-hidden="true" {...glyphProps} />
      </span>
    );
  }
  return XrayIcon;
}

export const Icons = {
  /* Jira issue types (outline glyphs, as Jira draws them). */
  JiraEpic: jiraIcon(Zap, "#A35BE8"),
  JiraStory: jiraIcon(Bookmark, "#357DE8"),
  JiraTask: jiraIcon(SquareCheckBig, "#357DE8"),
  JiraBug: jiraIcon(Bug, "#E5493A"),
  /* Xray issue types (filled squares, as Xray draws them). */
  JiraXrayTest: xrayIcon(Circle, "#117885"),
  JiraTestSet: xrayIcon(Blend, "#5E4DB2"),
  JiraTestPlan: xrayIcon(ListChecks, "#E06C00"),
  JiraTestExecution: xrayIcon(Play, "#82B536", { fill: "#fff" }),
  JiraPrecondition: xrayIcon(CircleDot, "#1D9AAA"),
  Spark: lucide(Sparkle, SOLID),
  Boards: lucide(Kanban),
  Grid: lucide(LayoutGrid),
  List: lucide(List),
  Play: lucide(Play, SOLID),
  Pause: lucide(Pause, SOLID),
  Refresh: lucide(RotateCw),
  /* Two arrows round a cycle: made again (an App Page Test or Artifact re-created). */
  Recreate: lucide(RefreshCw),
  Inbox: lucide(Inbox),
  Settings: lucide(Settings),
  /* Configuration: sliders on a gear (Settings › Configuration). */
  Configuration: lucide(Settings2),
  Eye: lucide(Eye),
  EyeOff: lucide(EyeOff),
  Plus: lucide(Plus),
  ChevronDown: lucide(ChevronDown),
  ChevronUp: lucide(ChevronUp),
  ChevronRight: lucide(ChevronRight),
  ChevronLeft: lucide(ChevronLeft),
  ArrowLeft: lucide(ArrowLeft),
  /* A chat bubble: send something to the assistant's chat. */
  Chat: lucide(MessageCircle),
  // Puts a value into a field (Choose a value for {NAME}).
  ArrowRight: lucide(ArrowRight),
  SortAsc: lucide(ArrowUpWideNarrow),
  SortDesc: lucide(ArrowDownWideNarrow),
  Check: lucide(Check),
  X: lucide(X),
  Clock: lucide(Clock),
  Calendar: lucide(Calendar),
  Minus: lucide(Minus),
  Search: lucide(Search),
  /* Spins with the caller's animate-spin. */
  Spinner: lucide(LoaderCircle),
  Sun: lucide(Sun),
  Moon: lucide(Moon),
  LogOut: lucide(LogOut),
  Trash: lucide(Trash2),
  Camera: lucide(Camera),
  ShieldCheck: lucide(ShieldCheck),
  Star: lucide(Star),
  // Certification badge with a check. Used for Run validations.
  BadgeCheck: lucide(BadgeCheck),
  Pencil: lucide(Pencil),
  Users: lucide(Users),
  User: lucide(User),
  /* A person in a circle: Jira's "For you" icon. */
  UserCircle: lucide(CircleUser),
  Home: lucide(House),
  Chart: lucide(ChartColumn),
  ChartLine: lucide(ChartLine),
  Report: lucide(FileText),
  /* Rolled scroll: Build Playwright scripts' "the test script to generate". */
  Script: lucide(Scroll),
  /* "(x)": a scenario's {VARIABLE} typed in at Generate. */
  Variable: lucide(Variable),
  /* An Artifact (the generated Playwright script): the Artifacts menu item and column. */
  ScrollText: lucide(ScrollText),
  Download: lucide(Download),
  Upload: lucide(Upload),
  Activity: lucide(Activity),
  /* The Log: who did what, and when. */
  Log: lucide(Logs),
  Maximize: lucide(Maximize),
  /* Maximize's corners turned inwards: back to the smaller size. */
  Minimize: lucide(Minimize),
  More: lucide(EllipsisVertical),
  Unlink: lucide(Unlink),
  // Warning triangle: the user is about to repeat work that already exists.
  Alert: lucide(TriangleAlert),
  // Two stacked sheets: "a copy already exists".
  Copy: lucide(Copy),
  // Question mark in a circle: opens the per-screen help dialog.
  Help: lucide(CircleHelp),
  // Open book: the help dialog's own heading mark.
  Book: lucide(BookOpen),
  // Funnel: filter the board by epic / story card.
  Filter: lucide(Filter),
  // Chain link: a story card's linked Xray / Playwright tests.
  Link: lucide(Link),
  // Layered requirement board: the Requirement Board nav/section mark.
  Layers: lucide(Layers),
  // Padlock: a disabled/locked action that needs a prior selection.
  Lock: lucide(Lock),
  Mail: lucide(Mail),
  Cpu: lucide(Cpu),
  Sliders: lucide(SlidersVertical),
  /* Keyboard keys sent to the live browser (Manual test case). */
  Enter: lucide(CornerDownLeft),
  Tab: lucide(ArrowRightToLine),
  /* Lines hidden in a diff: click to show them. */
  Ellipsis: lucide(Ellipsis),
  /* Drag handle: the floating panel's header can be dragged. */
  Grip: lucide(GripVertical),
  /* A panel's resize corner (drag to resize). */
  Resize: lucide(MoveDiagonal2),
  /* The agent, in the loader drawings. */
  Bot: lucide(Bot),
  /* A plain ring, in the loader drawings. */
  Ring: lucide(Circle),
};

export default Icons;
